import { useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.jsx';
import api from '../api/client.js';
import BrandHeader from './BrandHeader.jsx';
import NotificationToast from './NotificationToast.jsx';
import QuickCapture from './QuickCapture.jsx';
import GlobalSearch from './GlobalSearch.jsx';
import ImpersonationBanner from './ImpersonationBanner.jsx';
import ExcedeBanner from './ExcedeBanner.jsx';
import DataFreshnessBanner from './DataFreshnessBanner.jsx';
import { useSSE } from '../hooks/useSSE.js';
import clsx from 'clsx';

// Sidebar uses section headings. Each section has items; the mobile bottom nav
// flattens visible items and ignores `sidebarOnly: true`.
//
// Item flags:
//   `sidebarOnly: true` — hide from the (space-constrained) mobile bottom nav
//   `roles: [...]`      — restrict to those roles (omitted = everyone)
//   `branches: [...]`   — restrict to those branch codes; superadmin always passes
//   `soon: true`        — render as a disabled placeholder (no link)
const NAV_SECTIONS = [
  { id: 'common', label: null, items: [
    { to: '/', label: 'Dashboard', icon: '◉' },
    { to: '/notes/new', label: 'New Note', icon: '🎙', primary: true },
    { to: '/notes', label: 'Notes', icon: '🗒', end: true },
    { to: '/customers', label: 'Customers', icon: '👥' },
    { to: '/followups', label: 'Follow-ups', icon: '⏱' },
    { to: '/blitz', label: 'Blitz', icon: '📣', sidebarOnly: true },
    { to: '/recap', label: 'Recap', icon: '☷' },
  ]},
  { id: 'service', label: 'Service', sidebarOnly: true, items: [
    { to: '/aging', label: 'R.O. Aging', icon: '⏳', sidebarOnly: true },
    { to: '/service', label: 'Service', icon: '🔧', sidebarOnly: true },
  ]},
  { id: 'parts', label: 'Parts', items: [
    { to: '/parts', label: 'Parts', icon: '🛒' },
  ]},
  { id: 'sales', label: 'Trailer Sales', sidebarOnly: true, items: [
    { to: '/pipeline', label: 'Pipeline', icon: '◢' },
    { to: '/fresno-inventory', label: 'Fresno inventory', icon: '🚛', sidebarOnly: true, branches: ['FRESNO'] },
    { to: '/trailer-sales', label: 'Aging fleet', icon: '🚚', sidebarOnly: true },
    { to: '/stale-inventory', label: 'Stale inventory', icon: '🕸', sidebarOnly: true },
  ]},
  { id: 'reporting', label: 'Reporting', sidebarOnly: true,
    roles: ['superadmin','dealer_admin','branch_admin'], items: [
    { to: '/reports/sales',    label: 'Sales Overview', icon: '📊', sidebarOnly: true },
    { to: '/reports/activity', label: 'Rep Activity',   icon: '📋', sidebarOnly: true },
  ]},
  { id: 'admin', label: 'Admin', sidebarOnly: true,
    roles: ['superadmin', 'dealer_admin', 'branch_admin'], items: [
    { to: '/admin/executive', label: 'Executive', icon: '★', sidebarOnly: true },
    { to: '/admin/dashboard', label: 'Manager', icon: '◫', sidebarOnly: true },
    { to: '/admin/bdm-performance', label: 'Sales Performance', icon: '📊', sidebarOnly: true },
    { to: '/admin/ingested-emails', label: 'Ingested Emails', icon: '✉️', sidebarOnly: true },
    { to: '/admin/user-activity', label: 'User Activity', icon: '🔍', sidebarOnly: true, roles: ['superadmin'] },
    { to: '/admin/contact-lists', label: 'Contact Lists', icon: '📧', sidebarOnly: true, roles: ['superadmin'] },
    { to: '/admin/automation-settings', label: 'Automation Settings', icon: '🤖', sidebarOnly: true, roles: ['superadmin', 'dealer_admin'] },
  ]},
];

export default function AppShell() {
  const { user, logout, startImpersonation, isImpersonating, hasBranchAccess, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const widgetInjected = useRef(false);

  // Fire-and-forget page view on every navigation
  useEffect(() => {
    if (!user) return;
    api.post('/admin/page-views', { path: location.pathname }).catch(() => {});
  }, [location.pathname, user?.id]);

  // "Preview as..." quick-impersonate — superadmin only, not while already impersonating
  const allUsersQuery = useQuery({
    queryKey: ['admin', 'users', 'all-impersonatable'],
    queryFn: () => api.get('/admin/users').then(r =>
      r.data.data
        .filter(u => u.status === 'active' && u.role !== 'superadmin')
        .sort((a, b) => a.full_name.localeCompare(b.full_name))
    ),
    enabled: user?.role === 'superadmin' && !isImpersonating,
    staleTime: 5 * 60 * 1000,
  });
  const impersonateMutation = useMutation({
    mutationFn: (id) => api.post(`/admin/users/${id}/impersonate`).then(r => r.data.data),
    onSuccess: (data) => { startImpersonation(data); navigate('/'); },
  });

  // attach SSE — toast component reads from here too
  useSSE();

  // Inject panel CR widget — superadmin only (Change Requests is a
  // superadmin-only surface, 2026-08-03)
  useEffect(() => {
    if (!user || user.role !== 'superadmin' || widgetInjected.current) return;
    api.get('/change-requests/widget-token').then(r => {
      const token = r.data?.token;
      if (!token) return;
      const script = document.createElement('script');
      script.src = 'https://panel.adams-ai.com/cr-widget.js';
      script.dataset.token = token;
      script.dataset.app = 'utsales';
      script.async = true;
      document.head.appendChild(script);
      widgetInjected.current = true;
    }).catch(() => {});
  }, [user]);

  return (
    <div className="min-h-full flex flex-col bg-white">
      <ImpersonationBanner />
      <ExcedeBanner />
      <DataFreshnessBanner />
      <BrandHeader />

      {/* Top bar: user + admin links */}
      <div className="bg-panel border-b border-navy-100">
        <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center gap-3 text-sm">
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <span className="text-muted">
              {user?.full_name} <span className="opacity-60">· {user?.role.replace('_',' ')}</span>
            </span>
            {user?.role === 'superadmin' && !isImpersonating && (allUsersQuery.data?.length ?? 0) > 0 && (
              <select
                className="text-sm border border-navy-200 rounded px-2 py-1 bg-white text-navy"
                value=""
                disabled={impersonateMutation.isPending}
                onChange={e => { if (e.target.value) impersonateMutation.mutate(parseInt(e.target.value, 10)); }}
              >
                <option value="">{impersonateMutation.isPending ? 'Switching…' : 'Preview as…'}</option>
                {['dealer_admin', 'branch_admin', 'sales'].map(role => {
                  const group = (allUsersQuery.data || []).filter(u => u.role === role);
                  if (!group.length) return null;
                  const label = role === 'dealer_admin' ? 'Dealer Admins' : role === 'branch_admin' ? 'Branch Admins' : 'Sales Reps';
                  return (
                    <optgroup key={role} label={label}>
                      {group.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            )}
          </div>
          {/* On mobile the top bar is cramped; give search its own full-width
              row below the links so it's actually usable (Android complaint). */}
          <div className="order-last w-full md:order-none md:flex-1 md:max-w-md">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {/* persona switcher moved next to name above */}
            <NavLink to="/reports/sales" className="text-navy hover:underline">Reports</NavLink>
            {(user?.role !== 'sales') && (
              <>
                <NavLink to="/admin/stale-followups" className="text-navy hover:underline">Stale</NavLink>
                {hasPermission('can_access_user_admin') && (
                  <NavLink to="/admin/users" className="text-navy hover:underline">Users</NavLink>
                )}
                <NavLink to="/admin/audit-log" className="text-navy hover:underline">Activity</NavLink>
              </>
            )}
            <NavLink to="/ai-questions" className="text-navy hover:underline">AI Inbox</NavLink>
            {user?.role === 'superadmin' && (
              <NavLink to="/change-requests" className="text-navy hover:underline">Requests</NavLink>
            )}
            <NavLink to="/help" className="text-navy hover:underline">Help</NavLink>
            <NavLink to="/settings" className="text-navy hover:underline">Settings</NavLink>
            <button onClick={() => { logout(); navigate('/login'); }} className="text-crimson hover:underline">
              Logout
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-6 pb-24 md:pb-6">
        <Outlet />
      </main>

      {(() => {
        const visibleSections = NAV_SECTIONS
          .filter(sec => !sec.roles || sec.roles.includes(user?.role))
          .map(sec => ({
            ...sec,
            items: sec.items.filter(it =>
              (!it.roles || it.roles.includes(user?.role)) &&
              (!it.branches || hasBranchAccess(it.branches))
            ),
          }))
          .filter(sec => sec.items.length > 0);
        const bottomItems = visibleSections.flatMap(s => s.items).filter(it => !it.sidebarOnly && !it.soon);

        return (
          <>
            {/* Bottom nav for mobile — flat, sidebar-only and "soon" items hidden */}
            <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-navy-100 shadow-panel">
              <div className="grid" style={{ gridTemplateColumns: `repeat(${bottomItems.length}, minmax(0, 1fr))` }}>
                {bottomItems.map(n => (
                  <NavLink key={n.to} to={n.to} end={n.to === '/' || n.end}
                    className={({isActive}) => clsx(
                      'flex flex-col items-center gap-0.5 py-2 text-[11px]',
                      isActive ? 'text-crimson' : 'text-navy',
                      n.primary && 'font-semibold'
                    )}>
                    <span className="text-lg leading-none">{n.icon}</span>
                    <span className="truncate px-1">{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </nav>

            {/* Sidebar nav for tablet+ — sectioned with headings */}
            <nav className="hidden md:block fixed left-0 top-[88px] bottom-0 w-48 border-r border-navy-100 bg-white overflow-y-auto">
              <div className="py-4 px-2 flex flex-col gap-3">
                {visibleSections.map(sec => (
                  <div key={sec.id}>
                    {sec.label && (
                      <div className="text-[10px] uppercase tracking-wider text-muted px-3 pb-1">
                        {sec.label}
                      </div>
                    )}
                    <div className="flex flex-col gap-1">
                      {sec.items.map(n => n.soon ? (
                        <div key={n.label}
                             title="Coming soon"
                             className="flex items-center gap-2 px-3 py-2 rounded text-sm text-muted opacity-60 cursor-not-allowed">
                          <span>{n.icon}</span>
                          <span className="flex-1">{n.label}</span>
                          <span className="text-[9px] uppercase tracking-wider bg-panel px-1.5 py-0.5 rounded">soon</span>
                        </div>
                      ) : (
                        <NavLink key={n.to} to={n.to} end={n.to === '/' || n.end}
                          className={({isActive}) => clsx(
                            'flex items-center gap-2 px-3 py-2 rounded text-sm',
                            isActive ? 'bg-crimson text-white' : 'text-navy hover:bg-panel',
                            n.primary && !isActive && 'border-l-2 border-crimson'
                          )}>
                          <span>{n.icon}</span><span>{n.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </nav>
          </>
        );
      })()}
      {/* push main content right when sidebar visible.
          `main.flex-1` outranks Tailwind's `.px-4` (which also sets
          padding-left) so this rule actually wins; a bare `main` selector
          loses on specificity and the sidebar overlaps the content. */}
      <style>{`
        @media (min-width: 768px) {
          main.flex-1 { padding-left: 13rem; }
        }
      `}</style>

      <NotificationToast />
      <QuickCapture />
    </div>
  );
}
